<script setup lang="ts">
import { onMounted, ref } from "vue"
import { BookMarked, ExternalLink } from "lucide-vue-next"
import AppLayout from "@/layouts/AppLayout.vue"
import { Button } from "@/components/ui/button"

defineOptions({
    layout: [AppLayout],
})

const props = defineProps<{
    manualUrl: string
    title: string
}>()

const opened = ref(false)

const sections = [
    "Core Values",
    "Grooming & Uniform Standards",
    "Attendance & Scheduling",
    "Daily Opening SOP",
    "Espresso Standards",
    "Milk & Beverage SOP",
    "Kitchen SOP",
    "Food Storage",
    "Inventory Responsibilities",
    "Cash Handling SOP",
    "Customer Service Standards",
    "Handling Customer Complaints",
    "Dining Area SOP",
    "Alfresco Area SOP",
    "Cleanliness & Sanitation",
    "Equipment Care",
    "Daily Closing SOP",
    "Teamwork Expectations",
    "Professional Conduct",
    "Emergency Procedures",
    "Final Reminder",
]

function openManual() {
    window.open(props.manualUrl, "_blank", "noopener,noreferrer")
    opened.value = true
}

onMounted(() => {
    openManual()
})
</script>

<template>
    <div class="mx-auto max-w-3xl py-6">
        <div class="rounded-xl border bg-white p-6 shadow-sm sm:p-8">
            <div class="flex items-start gap-4">
                <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[rgb(181_115_5)]/10 text-[rgb(181_115_5)]">
                    <BookMarked class="h-6 w-6" />
                </div>
                <div class="min-w-0 flex-1">
                    <p class="text-xs font-medium uppercase tracking-[0.18em] text-[rgb(181_115_5)]/70">
                        Barista Station
                    </p>
                    <h1 class="mt-1 text-2xl font-semibold tracking-tight text-gray-900">
                        {{ title }}
                    </h1>
                    <p class="mt-2 text-sm leading-relaxed text-gray-600">
                        The full operations manual lives in Medley. It can’t be embedded here, so it opens in a new tab.
                    </p>
                </div>
            </div>

            <div class="mt-6 flex flex-wrap gap-3">
                <Button
                    class="bg-[rgb(181_115_5)] hover:bg-[rgb(145_90_0)]"
                    @click="openManual"
                >
                    <ExternalLink class="mr-1.5 h-4 w-4" />
                    {{ opened ? "Reopen manual" : "Open manual" }}
                </Button>
                <Button as="a" variant="outline" :href="manualUrl" target="_blank" rel="noopener noreferrer">
                    Go to Medley
                </Button>
            </div>

            <p v-if="opened" class="mt-3 text-xs text-muted-foreground">
                If nothing opened, allow pop-ups for this site, then click Reopen manual.
            </p>

            <div class="mt-8 border-t pt-6">
                <h2 class="text-sm font-semibold text-gray-900">What’s inside</h2>
                <ul class="mt-3 grid gap-2 sm:grid-cols-2">
                    <li
                        v-for="section in sections"
                        :key="section"
                        class="flex items-center gap-2 text-sm text-gray-600"
                    >
                        <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(181_115_5)]" />
                        {{ section }}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>
